class IBRecommendationsSlider extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ibRecommendationsBound === 'true') return;
    this.dataset.ibRecommendationsBound = 'true';

    // Nel Theme Editor carichiamo subito per mantenere l'anteprima affidabile.
    if (this.classList.contains('ib-recommendations--editor')) {
      this.loadRecommendations();
      return;
    }

    this.setupDeferredLoad();
  }

  disconnectedCallback() {
    this.cleanupSlider?.();
    this.recommendationsObserver?.disconnect();
    this.recommendationsObserver = null;
  }

  setupDeferredLoad() {
    // Fallback: se IntersectionObserver non esiste, manteniamo
    // il comportamento originale.
    if (!('IntersectionObserver' in window)) {
      this.loadRecommendations();
      return;
    }

    // Precarica quando la sezione è ancora ~1600px sotto il viewport.
    // Così non pesa sul caricamento iniziale ma arriva pronta allo scroll.
    this.recommendationsObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        this.recommendationsObserver?.disconnect();
        this.recommendationsObserver = null;

        this.loadRecommendations();
      },
      {
        root: null,
        rootMargin: '1600px 0px',
        threshold: 0,
      }
    );

    this.recommendationsObserver.observe(this);
  }

  async loadRecommendations() {
    if (this.dataset.loaded !== 'true') {
      const url = this.dataset.url;

      if (!url) {
        this.hidden = true;
        return;
      }

      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html',
          },
        });

        if (!response.ok) {
          throw new Error(`Recommendations request failed: ${response.status}`);
        }

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(
          html,
          'text/html'
        );

        const nextRoot = documentFragment.querySelector(
          `#${CSS.escape(this.id)}`
        );

        const nextContent = nextRoot?.querySelector(
          '[data-ib-recommendations-content]'
        );

        const currentContent = this.querySelector(
          '[data-ib-recommendations-content]'
        );

        if (!nextContent || !currentContent) {
          this.hidden = true;
          return;
        }

        currentContent.replaceWith(nextContent);
        this.dataset.loaded = 'true';
      } catch (error) {
        console.error('[IB Product Recommendations]', error);

        if (this.dataset.designMode !== 'true') {
          this.hidden = true;
        }

        return;
      }
    }

    const content = this.querySelector(
      '[data-ib-recommendations-content]'
    );

    const track = this.querySelector('[data-ib-rec-track]');
    const items = this.querySelectorAll('[data-ib-rec-item]');

    if (!content || !track || items.length === 0) {
      if (!this.classList.contains('ib-recommendations--editor')) {
        this.hidden = true;
      }

      return;
    }

    this.setupSlider();
  }

  setupSlider() {
    this.cleanupSlider?.();

    this.track = this.querySelector('[data-ib-rec-track]');
    this.previousButton = this.querySelector('[data-ib-rec-previous]');
    this.nextButton = this.querySelector('[data-ib-rec-next]');

    if (!this.track) return;

    const getVisibleItems = () =>
      Array.from(
        this.track.querySelectorAll('[data-ib-rec-item]')
      ).filter((item) => item.offsetParent !== null);

    const getStep = () => {
      const firstItem = getVisibleItems()[0];

      if (!firstItem) {
        return this.track.clientWidth * 0.85;
      }

      const styles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;

      return firstItem.getBoundingClientRect().width + gap;
    };

    const updateControls = () => {
      const maxScroll =
        this.track.scrollWidth - this.track.clientWidth;

      const atStart = this.track.scrollLeft <= 2;
      const atEnd =
        maxScroll <= 2 ||
        this.track.scrollLeft >= maxScroll - 2;

      if (this.previousButton) {
        this.previousButton.disabled = atStart;
      }

      if (this.nextButton) {
        this.nextButton.disabled = atEnd;
      }
    };

    const scrollByDirection = (direction) => {
      this.track.scrollBy({
        left: getStep() * direction,
        behavior: 'smooth',
      });
    };

    const onPrevious = () => scrollByDirection(-1);
    const onNext = () => scrollByDirection(1);

    this.previousButton?.addEventListener('click', onPrevious);
    this.nextButton?.addEventListener('click', onNext);
    this.track.addEventListener('scroll', updateControls, {
      passive: true,
    });

    const resizeObserver = new ResizeObserver(updateControls);
    resizeObserver.observe(this.track);

    /* Mouse drag only. Touch remains native for reliable mobile swipe. */
    let pointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let dragging = false;
    let suppressClick = false;

    const threshold = 6;

    const onPointerDown = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = this.track.scrollLeft;
      dragging = false;
    };

    const onPointerMove = (event) => {
      if (
        event.pointerType !== 'mouse' ||
        pointerId === null ||
        event.pointerId !== pointerId
      ) {
        return;
      }

      const deltaX = event.clientX - startX;

      if (!dragging && Math.abs(deltaX) >= threshold) {
        dragging = true;
        suppressClick = true;
        this.track.classList.add('is-dragging');

        try {
          this.track.setPointerCapture(event.pointerId);
        } catch (_) {}
      }

      if (!dragging) return;

      event.preventDefault();
      this.track.scrollLeft = startScrollLeft - deltaX;
    };

    const onPointerEnd = (event) => {
      if (
        event.pointerType !== 'mouse' ||
        pointerId === null ||
        event.pointerId !== pointerId
      ) {
        return;
      }

      pointerId = null;
      this.track.classList.remove('is-dragging');

      if (dragging) {
        dragging = false;

        window.setTimeout(() => {
          suppressClick = false;
        }, 80);
      }

      updateControls();
    };

    const onClickCapture = (event) => {
      if (!suppressClick) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    };

    this.track.addEventListener('pointerdown', onPointerDown);
    this.track.addEventListener('pointermove', onPointerMove);
    this.track.addEventListener('pointerup', onPointerEnd);
    this.track.addEventListener('pointercancel', onPointerEnd);
    this.track.addEventListener('lostpointercapture', onPointerEnd);
    this.track.addEventListener('click', onClickCapture, true);

    updateControls();

    this.cleanupSlider = () => {
      this.previousButton?.removeEventListener('click', onPrevious);
      this.nextButton?.removeEventListener('click', onNext);
      this.track?.removeEventListener('scroll', updateControls);
      this.track?.removeEventListener('pointerdown', onPointerDown);
      this.track?.removeEventListener('pointermove', onPointerMove);
      this.track?.removeEventListener('pointerup', onPointerEnd);
      this.track?.removeEventListener('pointercancel', onPointerEnd);
      this.track?.removeEventListener(
        'lostpointercapture',
        onPointerEnd
      );
      this.track?.removeEventListener('click', onClickCapture, true);
      resizeObserver.disconnect();
    };
  }
}

if (!customElements.get('ib-recommendations-slider')) {
  customElements.define(
    'ib-recommendations-slider',
    IBRecommendationsSlider
  );
}